/**
 * <s-hero-carousel> — autoplaying, swipeable hero slider.
 *
 * Markup contract:
 *   [data-hero-track]      flex row of [data-hero-slide] (each 100% wide); moved with translateX.
 *   button[command="--prev|--next|--go|--toggle"][commandfor=<this id>]
 *     --go uses data-index; dots get aria-current="true" when active.
 *   [data-hero-toggle]     pause/play button (aria-pressed="true" while paused,
 *                          data-label-pause / data-label-play swap its aria-label).
 *   [data-hero-status]     polite live region, announced only on manual navigation.
 *
 * Autoplay advances every data-interval ms (default 3000), loops, pauses while
 * hovered, focused within, off-screen or the tab is hidden, and never starts when
 * the visitor prefers reduced motion. On phones it waits for the first scroll or touch. Inactive slides are inert.
 */
class SHeroCarousel extends HTMLElement {
  #ac;
  #index = 0;
  #timer = 0;
  #userPaused = false;
  #hovered = false;
  #focused = false;
  #visible = true;
  #observer;
  #startX = null;
  #startY = 0;
  #rtl = false;
  #waiting = false;

  connectedCallback() {
    this.#ac = new AbortController();
    // Read direction once; per-slide getComputedStyle calls force style recalculation.
    this.#rtl = getComputedStyle(this).direction === 'rtl';
    const opts = { signal: this.#ac.signal };

    if (matchMedia('(prefers-reduced-motion: reduce)').matches) this.#setPaused(true);

    this.addEventListener('command', this.#onCommand, opts);
    this.addEventListener('pointerenter', () => { this.#hovered = true; this.#schedule(); }, opts);
    this.addEventListener('pointerleave', () => { this.#hovered = false; this.#schedule(); }, opts);
    this.addEventListener('focusin', () => { this.#focused = true; this.#schedule(); }, opts);
    this.addEventListener('focusout', (e) => {
      if (!this.contains(/** @type {Node} */ (e.relatedTarget))) { this.#focused = false; this.#schedule(); }
    }, opts);
    this.addEventListener('keydown', this.#onKeydown, opts);
    this.addEventListener('pointerdown', this.#onPointerDown, opts);
    this.addEventListener('pointerup', this.#onPointerUp, opts);
    this.addEventListener('pointercancel', () => { this.#startX = null; }, opts);
    document.addEventListener('visibilitychange', () => this.#schedule(), opts);

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = Boolean(entry?.isIntersecting);
      this.#schedule();
    });
    this.#observer.observe(this);

    // Warm the other slides' images once the page has loaded and the main thread is idle,
    // so slides never arrive blank. Only images that render at this viewport are warmed:
    // the side panels are display:none on mobile and stay unloaded there.
    const warm = () => {
      const idle = window.requestIdleCallback ?? ((cb) => setTimeout(cb, 200));
      idle(() => {
        this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          const renders = img.checkVisibility ? img.checkVisibility() : img.offsetParent !== null;
          if (renders) img.loading = 'eager';
        });
      });
    };
    // Phones: hold autoplay and the slide-image warm-up until the shopper first scrolls or
    // touches, so no slide transitions or extra downloads compete with the initial load.
    // Desktop keeps autoplaying straight away.
    if (matchMedia('(max-width: 767px)').matches) {
      this.#waiting = true;
      const engage = () => {
        if (!this.#waiting) return;
        this.#waiting = false;
        warm();
        this.#schedule();
      };
      window.addEventListener('scroll', engage, { once: true, passive: true, ...opts });
      window.addEventListener('pointerdown', engage, { once: true, passive: true, ...opts });
      window.addEventListener('keydown', engage, { once: true, ...opts });
    } else if (document.readyState === 'complete') warm();
    else window.addEventListener('load', warm, { once: true, signal: this.#ac.signal });

    this.#render(false);
    this.#schedule();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
  }

  get #slides() {
    return [...this.querySelectorAll('[data-hero-slide]')];
  }

  #go(index, announce = false) {
    const count = this.#slides.length;
    if (!count) return;
    this.#index = (index + count) % count;
    this.#render(announce);
    this.#schedule();
  }

  #render(announce) {
    const track = this.querySelector('[data-hero-track]');
    if (track instanceof HTMLElement) {
      track.style.transform = `translateX(${(this.#rtl ? 1 : -1) * this.#index * 100}%)`;
    }
    this.#slides.forEach((slide, i) => { slide.inert = i !== this.#index; });
    this.querySelectorAll('[command="--go"]').forEach((dot) => {
      const active = Number(dot.getAttribute('data-index')) === this.#index;
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    const status = this.querySelector('[data-hero-status]');
    if (status && announce) {
      status.textContent = this.#slides[this.#index]?.getAttribute('aria-label') ?? '';
    }
  }

  #schedule() {
    clearTimeout(this.#timer);
    const blocked = this.#waiting || this.#userPaused || this.#hovered || this.#focused || !this.#visible || document.hidden;
    if (blocked || this.#slides.length < 2) return;
    const interval = Number(this.dataset.interval) || 3000;
    this.#timer = window.setTimeout(() => this.#go(this.#index + 1), interval);
  }

  #setPaused(paused) {
    this.#userPaused = paused;
    const toggle = this.querySelector('[data-hero-toggle]');
    if (toggle instanceof HTMLElement) {
      toggle.setAttribute('aria-pressed', String(paused));
      const label = paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
      if (label) toggle.setAttribute('aria-label', label);
    }
  }

  #onCommand = (event) => {
    const command = /** @type {any} */ (event).command;
    const source = /** @type {any} */ (event).source;
    if (command === '--prev') this.#go(this.#index - 1, true);
    else if (command === '--next') this.#go(this.#index + 1, true);
    else if (command === '--go') this.#go(Number(source?.getAttribute('data-index')) || 0, true);
    else if (command === '--toggle') {
      this.#setPaused(!this.#userPaused);
      this.#schedule();
    }
  };

  #onKeydown = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[command]')) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      const forward = (event.key === 'ArrowRight') !== this.#rtl;
      this.#go(this.#index + (forward ? 1 : -1), true);
      event.preventDefault();
    }
  };

  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    this.#startX = event.clientX;
    this.#startY = event.clientY;
  };

  #onPointerUp = (event) => {
    if (this.#startX === null) return;
    const dx = event.clientX - this.#startX;
    const dy = event.clientY - this.#startY;
    this.#startX = null;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
    const forward = (dx < 0) !== this.#rtl;
    this.#go(this.#index + (forward ? 1 : -1), true);
  };
}

customElements.define('s-hero-carousel', SHeroCarousel);
